import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function PartnerPortal() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    company: "",
    expertise: "",
    years_experience: "",
    linkedin: "",
    message: "",
  });
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/partner-application/me");
      if (data) {
        setApplication(data);
        setForm({
          company: data.company || "",
          expertise: data.expertise || "",
          years_experience: data.years_experience || "",
          linkedin: data.linkedin || "",
          message: data.message || "",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (loaded && !application && user?.company) {
      setForm((f) => (f.company ? f : { ...f, company: user.company }));
    }
  }, [loaded, application, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/partner-application", form);
      setApplication(data);
      toast.success("Application submitted. Our team will review it shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalShell
      eyebrow="Partner Portal"
      title="Join the Aliwer partner network"
      subtitle="Tell us about your expertise. Once approved, you'll be matched to client engagements that fit your skills — on your terms, as an independent contractor."
    >
      <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-surface p-8" data-testid="application-form">
          <h2 className="font-display text-xl tracking-tight text-paper mb-6">Your professional profile</h2>

          <div className="grid gap-5 sm:grid-cols-2">
            <PField label="Company / Practice" value={form.company} onChange={set("company")} testid="app-company" />
            <PField label="Years of experience" type="number" min="0" max="60" step="1" inputMode="numeric" value={form.years_experience} onChange={set("years_experience")} testid="app-years" required />
          </div>

          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-fog">Areas of expertise</label>
            <textarea
              rows={3}
              value={form.expertise}
              onChange={set("expertise")}
              required
              placeholder="e.g. Penetration testing, AWS security, SOC 2 readiness…"
              data-testid="app-expertise"
              className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors resize-none placeholder:text-fog/50"
            />
          </div>

          <div className="mt-5">
            <PField label="LinkedIn / Portfolio URL" value={form.linkedin} onChange={set("linkedin")} testid="app-linkedin" />
          </div>

          <div className="mt-5 mb-6">
            <label className="text-xs uppercase tracking-widest text-fog">Why Aliwer? (optional)</label>
            <textarea
              rows={3}
              value={form.message}
              onChange={set("message")}
              data-testid="app-message"
              className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="application-submit"
            className="w-full rounded-full bg-paper text-black text-sm font-medium py-3.5 hover:bg-white transition-colors disabled:opacity-60"
          >
            {loading ? "Submitting…" : application ? "Update application" : "Submit application"}
          </button>
        </form>

        <div>
          <h2 className="font-display text-xl tracking-tight text-paper mb-6">Application status</h2>
          {!loaded ? null : application ? (
            <div className="rounded-2xl border border-white/10 bg-surface p-8" data-testid="application-status">
              <div className="flex items-center gap-2 text-paper">
                {application.status === "approved" ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <Clock size={18} />
                )}
                <span className="text-sm uppercase tracking-widest">{application.status}</span>
              </div>
              <p className="mt-4 text-sm text-fog leading-relaxed">
                {application.status === "pending"
                  ? "Your application is under review. Our partnerships team typically responds within 3 business days."
                  : "Your application has been reviewed. Check your email for next steps."}
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm">
                <Row label="Expertise" value={application.expertise} />
                <Row label="Experience" value={`${application.years_experience} yrs`} />
                {application.company && <Row label="Company" value={application.company} />}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-fog" data-testid="no-application">
              No application yet. Complete your profile to apply.
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}

function PField({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-fog">{label}</label>
      <input
        {...props}
        data-testid={testid}
        className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors placeholder:text-fog/50"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-fog">{label}</span>
      <span className="text-paper text-right max-w-[60%]">{value}</span>
    </div>
  );
}
